const TITLE_SUFFIX = ' - Helo'

export type BrowserTitleComputeInput = {
  /**
   * When set, this string is the entire human tab segment before ` - Helo`
   * (e.g. saved view name or admin page title). Mutually exclusive with the
   * detail fields below for display purposes.
   */
  staticTitle: string | null
  /** CMS view `label` when using the composed detail tab pattern. */
  viewLabel: string | null
  /**
   * Primary entity display for the detail page (order id, shipment id, …). Appears
   * after the record-type segment and before an optional drawer suffix.
   */
  detailEntityDisplay: string | null
  /** Optional segment from `DrawerCardWidget` when expanded (e.g. Milestones, Details). */
  detailDrawerSuffix: string | null
}

/** Subset of fields that determine whether the tab title falls back to plain `Helo`. */
export type BrowserTitleIdleInput = Pick<
  BrowserTitleComputeInput,
  'staticTitle' | 'viewLabel'
>

/** True when there is no static segment and no view label (same condition as the `Helo` fallback). */
export const isIdleBrowserDocumentTitle = ({
  staticTitle,
  viewLabel,
}: BrowserTitleIdleInput): boolean => !staticTitle?.trim() && !viewLabel?.trim()

/**
 * Builds `document.title` per product rules: human segment + ` - Helo`.
 */
export const computeBrowserDocumentTitle = ({
  staticTitle,
  viewLabel,
  detailEntityDisplay,
  detailDrawerSuffix,
}: BrowserTitleComputeInput): string => {
  const trimmedStatic = staticTitle?.trim()
  if (trimmedStatic) {
    return `${trimmedStatic}${TITLE_SUFFIX}`
  }

  const p = viewLabel?.trim()
  if (p) {
    const entityDisplay = detailEntityDisplay?.trim() ?? ''
    const suf = detailDrawerSuffix?.trim()
    const parts = [p]
    if (entityDisplay) parts.push(entityDisplay)
    if (suf) parts.push(suf)
    return `${parts.join(' ')}${TITLE_SUFFIX}`
  }

  return 'Helo'
}
